import {
  bbox,
  booleanPointInPolygon,
  featureCollection,
  lineIntersect,
} from '@turf/turf';
import { Feature, LineString, MultiPolygon, Polygon } from 'geojson';
import { SHARED_GENERATE_INPUTS } from './reversePointsAndRows';
import { augmentFieldFeature } from '../layerAugmenters';
import lineOffset from '../betterLineOffset';
import { feetPerMile } from '../../constants/units';
import generateRowPoints from './generateRowPoints';
import {
  DataMaps,
  HeadlandProps,
  PointsAndRows,
  PointsAndRowsProps,
} from '../../types/pointsAndRows';
import {
  DEFAULT_BETWEEN_ROW_SPACING,
  DEFAULT_IN_ROW_SPACING,
} from '../../constants/fields';
import { unitsMiles } from '../units';
import { showWarning } from '../toast';
import {
  extendLine,
  lineIntersectsItselfInsidePolygon,
  linesIntersectInsidePolygon,
  splitLineAtIntersections,
} from './lines';

interface GenerateKeyLinePointsAndRowsProps
  extends HeadlandProps,
    PointsAndRowsProps {
  fieldFeature: Feature<Polygon>;
  rowFeature: Feature<LineString>;
}

/*
 * Ensure drawn row feature has:
 * - At least two points
 * - Lies partially or completely inside the fieldFeature
 * - Does not intersect itself inside the fieldFeature
 */
const isRowFeatureValid = (
  rowFeature: Feature<LineString>,
  fieldFeature: Feature<Polygon | MultiPolygon>,
) => {
  const rowPoints = rowFeature.geometry.coordinates;
  if (rowPoints.length < 2) {
    showWarning('Row must have more than one point');
    return false;
  }
  const rowIntersectsPolygon = !!lineIntersect(rowFeature, fieldFeature)
    .features.length;
  const rowPointInPolygon = rowPoints.some((rowPoint) =>
    booleanPointInPolygon(rowPoint, fieldFeature),
  );
  if (!rowIntersectsPolygon && !rowPointInPolygon) {
    showWarning('Row must intersect the polygon');
    return false;
  }
  if (lineIntersectsItselfInsidePolygon(rowFeature, fieldFeature)) {
    showWarning('Row cannot intersect itself or be overly concave');
    return false;
  }
  return true;
};

/*
 * Generate a row offset from the given rowFeature by the given distance in ft.
 * Ensure the offset row:
 * - Intersects the polygon at least once
 * - Does not intersect any of the other generated rows inside the polyon
 * - Does not intersect itself inside the polygon
 * If these conditions are not met, return null.
 */
const generateOffsetRow = (
  rowFeature: Feature<LineString>,
  distanceFt: number,
  distanceDegrees: number,
  polygon: Feature<Polygon | MultiPolygon>,
  rows: Feature<LineString>[],
) => {
  const offsetRow = lineOffset(rowFeature, distanceFt, unitsMiles);
  const extendedRow = extendLine(offsetRow, distanceDegrees);
  const polygonIntersections = lineIntersect(extendedRow, polygon);
  const intersectsOtherRow = rows.some((row) =>
    linesIntersectInsidePolygon(row, extendedRow, polygon),
  );
  const intersectsItself = lineIntersectsItselfInsidePolygon(
    extendedRow,
    polygon,
  );
  return polygonIntersections.features.length > 1 &&
    !intersectsOtherRow &&
    !intersectsItself
    ? extendedRow
    : null;
};

export const GENERATE_KEY_LINE_INPUTS = [
  'fieldFeature',
  'rowFeature',
  'inRowSpacing',
  'betweenRowSpacing',
  'headlandWidth',
  'headlandInnerFeature',
  'rowOverrides',
  'crop',
  'primaryInRowPattern',
  'type',
  ...SHARED_GENERATE_INPUTS,
];

const generateKeyLinePointsAndRows = (
  // Update GENERATE_KEY_LINE_INPUTS above if these params change
  {
    fieldFeature,
    rowFeature,
    inRowSpacing = DEFAULT_IN_ROW_SPACING,
    betweenRowSpacing = DEFAULT_BETWEEN_ROW_SPACING,
    headlandInnerFeature = false,
    headlandWidth = 0,
    rowOverrides,
    crop,
    primaryInRowPattern,
    type,
  }: GenerateKeyLinePointsAndRowsProps,
  dataMaps: DataMaps,
) => {
  if (!fieldFeature || !rowFeature) return {} as PointsAndRows;
  const [innerFieldFeature] =
    augmentFieldFeature({
      fieldFeature,
      headlandInnerFeature,
      headlandWidth,
      type,
    })?.features || [];
  // Get maximum dimension (in coordinate degrees) of the bounding box
  const boundingBox = bbox(fieldFeature);
  const [minX, minY, maxX, maxY] = boundingBox;
  const distanceDegrees = Math.max(maxX - minX, maxY - minY);
  // Extend row feature at each end by dimension of bounding box
  const extendedRowFeature = extendLine(rowFeature, distanceDegrees);

  // Validate the drawn rowFeature to avoid problems with row generation
  if (!isRowFeatureValid(extendedRowFeature, innerFieldFeature)) {
    return null;
  }

  const rows = [];
  rows.push(extendedRowFeature);
  // Generate rows in the positive direction while rows are valid
  for (let i = 1, includeRow = true; includeRow; i += 1) {
    const offsetRow = generateOffsetRow(
      extendedRowFeature,
      (betweenRowSpacing / feetPerMile) * i,
      distanceDegrees,
      innerFieldFeature,
      rows,
    );
    if (offsetRow != null) {
      rows.push(offsetRow);
    } else {
      includeRow = false;
    }
  }
  // Generate rows in the negative direction while rows are valid
  for (let i = -1, includeRow = true; includeRow; i -= 1) {
    const offsetRow = generateOffsetRow(
      extendedRowFeature,
      (betweenRowSpacing / feetPerMile) * i,
      distanceDegrees,
      innerFieldFeature,
      rows,
    );
    if (offsetRow != null) {
      rows.unshift(offsetRow);
    } else {
      includeRow = false;
    }
  }

  // Clip the rows so that only the portions inside the polygon remain
  const clippedRows = rows.flatMap((row) =>
    splitLineAtIntersections(row, innerFieldFeature),
  );

  return generateRowPoints(
    {
      rows: featureCollection(clippedRows),
      inRowSpacing,
      crop,
      rowOverrides,
      primaryInRowPattern,
    },
    dataMaps,
  );
};

export default generateKeyLinePointsAndRows;
